import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Button, Alert,
  CircularProgress, Snackbar, InputAdornment, Divider,
  FormControlLabel, Switch, Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ShieldIcon from '@mui/icons-material/Shield';
import { adminApi } from '../../api/adminApi';

function WafSection() {
  const [waf, setWaf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const load = () => adminApi.getWafSettings().then(setWaf).catch(() => setWaf({ enabled: false, status: 'unknown' }));

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const toggle = async (next) => {
    setBusy(true);
    try {
      const res = await adminApi.updateWafSettings(next);
      setWaf(res);
      setToast({ severity: 'success', message: res.message || 'WAF updated.' });
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
      load();
    } finally {
      setBusy(false);
    }
  };

  if (loading) { return <Box display="flex" justifyContent="center" my={3}><CircularProgress size={24} /></Box>; }

  const enabled = !!waf?.enabled;
  const propagating = waf?.deployStatus === 'InProgress' || waf?.status === 'disabling';

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
        <ShieldIcon fontSize="small" color={enabled ? 'success' : 'disabled'} />
        <Typography variant="subtitle2" fontWeight={700}>Web Application Firewall (WAF)</Typography>
        <Chip
          size="small"
          label={propagating ? 'Deploying…' : (enabled ? 'On' : 'Off')}
          color={propagating ? 'warning' : (enabled ? 'success' : 'default')}
          variant={propagating ? 'outlined' : 'filled'}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        Attaches AWS managed rules (OWASP Common, Known Bad Inputs, SQLi, IP Reputation) plus rate
        limiting to all platform CloudFront distributions, including the API. Turning it off deletes
        the WebACL so AWS stops billing it (~$11/mo); turning it on recreates it. Changes take
        about 5–15 minutes to propagate across CloudFront.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={busy || propagating}
            onChange={(e) => toggle(e.target.checked)}
          />
        }
        label={busy ? 'Applying…' : (enabled ? 'WAF enabled (billing active)' : 'WAF disabled (no WAF cost)')}
      />

      {propagating && (
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          CloudFront is still propagating the change. Reload in a few minutes to confirm the final state.
        </Alert>
      )}
      {waf?.updatedAt && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          Last changed {new Date(waf.updatedAt).toLocaleString()}{waf.updatedBy ? ` by ${waf.updatedBy}` : ''}.
        </Typography>
      )}

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Paper>
  );
}

// Rough monthly Cost Explorer spend estimate given a refresh window, assuming
// the dashboard + a handful of tenant cost views are opened through the day.
function estimateCeCalls(hours) {
  const h = Number(hours) || 12;
  const refreshesPerDay = Math.max(1, Math.floor(24 / h));
  // ~1 platform + a few tenant/period keys refreshed each window.
  return refreshesPerDay * 4 * 30;
}

export default function AdvancedSettingsPage() {
  const [form, setForm] = useState({ costExplorerHours: 12, monitoringSeconds: 60 });
  const [defaults, setDefaults] = useState({ costExplorerHours: 12, monitoringSeconds: 60 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    adminApi.getCacheSettings()
      .then((d) => { setForm(d.config || d.defaults); setDefaults(d.defaults || {}); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const d = await adminApi.updateCacheSettings({
        costExplorerHours: Number(form.costExplorerHours),
        monitoringSeconds: Number(form.monitoringSeconds),
      });
      setForm(d.config);
      setToast({ severity: 'success', message: 'Cache settings saved. Changes take effect within ~30s.' });
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) { return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>; }
  if (error) { return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>; }

  const estCalls = estimateCeCalls(form.costExplorerHours);

  return (
    <Box sx={{ p: 3, maxWidth: 760 }}>
      <Typography variant="h5" fontWeight={700} mb={1}>Advanced Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Control how long the platform caches expensive AWS API responses. Longer windows reduce
        AWS API charges; shorter windows show fresher data.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle2" fontWeight={700}>Cost Explorer refresh window</Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
          AWS Cost Explorer bills about $0.01 per request and only updates its data a few times a
          day, so a long window is recommended. Tenant and platform cost views share one cached
          result per period within this window. Default {defaults.costExplorerHours}h.
        </Typography>
        <TextField
          type="number" size="small" label="Refresh every" sx={{ width: 220 }}
          value={form.costExplorerHours}
          onChange={(e) => setForm((f) => ({ ...f, costExplorerHours: e.target.value }))}
          InputProps={{ endAdornment: <InputAdornment position="end">hours</InputAdornment> }}
          helperText="Between 0.25 and 720 (30 days)"
        />
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          Estimated ~{estCalls.toLocaleString()} Cost Explorer calls/month (~${(estCalls * 0.01).toFixed(2)})
          at this window, for typical admin usage.
        </Alert>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" fontWeight={700}>Monitoring refresh window</Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
          How long CloudWatch metrics + alarm state are cached. The Monitoring dashboard polls on
          this interval, and all viewers share a single set of CloudWatch calls per window.
          Default {defaults.monitoringSeconds}s.
        </Typography>
        <TextField
          type="number" size="small" label="Refresh every" sx={{ width: 220 }}
          value={form.monitoringSeconds}
          onChange={(e) => setForm((f) => ({ ...f, monitoringSeconds: e.target.value }))}
          InputProps={{ endAdornment: <InputAdornment position="end">seconds</InputAdornment> }}
          helperText="Between 10 and 3600 (1 hour)"
        />

        <Stack direction="row" justifyContent="flex-end" mt={3}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </Button>
        </Stack>
      </Paper>

      <WafSection />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
